import { forwardRef, useCallback, useEffect, useReducer } from "react";
import dayjs from "@/lib/day";
import styles from "@/styles/Form.module.scss";
import classnames from "classnames";
import SelectField from "./SelectField";
import ErrorMessage from "./ErrorMessage";

function createOption(value, format) {
  return { value, label: `${value}${format}` };
}

function selectOption(type, value) {
  if (!value) return null;
  const options =
    type === "year"
      ? yearOptions()
      : type === "month"
      ? monthOptions()
      : dayOptions();
  return options.find((option) => option.value == value);
}

const cachedOptions = { year: null, month: null, day: null };
function yearOptions() {
  if (!cachedOptions.year) {
    const now = dayjs();
    const year = now.year();
    const minYear = 1900;
    const options = [];
    for (let i = 0; i <= year - minYear; i++) {
      options.push(createOption(minYear + i, "年"));
    }
    cachedOptions.year = options;
  }
  return cachedOptions.year;
}

function monthOptions() {
  if (!cachedOptions.month) {
    const options = [];
    for (let i = 1; i <= 12; i++) {
      options.push(createOption(i, "月"));
    }
    cachedOptions.month = options;
  }
  return cachedOptions.month;
}

function dayOptions(daysInMonth = 31) {
  const options = [];
  for (let i = 1; i <= daysInMonth; i++) {
    options.push(createOption(i, "日"));
  }
  return options;
}

function getBirthday({ year, month, day }) {
  const date = dayjs(`${year}-${month}-01`);
  const lastDay = date.daysInMonth();
  if (day > lastDay) day = lastDay;
  return dayjs(`${year}-${month}-${day}`);
}

const initialState = {
  birthday: "",
  daysInMonth: 31,
  year: "",
  month: "",
  day: "",
};

const BirthdayField = forwardRef((props, ref) => {
  const {
    error,
    title,
    name,
    caption,
    required,
    forbidChange,
    value, // from Controller
    onChange, // from Controller
    ..._props
  } = props;

  const updateState = useCallback((state, key, val) => {
    const newState = { ...state, [key]: val };
    const birthday = getBirthday(newState);
    return {
      birthday: birthday.format("YYYY-MM-DD"),
      daysInMonth: birthday.daysInMonth(),
      year: birthday.year(),
      month: birthday.month() + 1,
      day: birthday.date(),
    };
  }, []);

  const handleYearMenuOpen = useCallback((state) => {
    if (!state.year) {
      setTimeout(() => {
        const menu = document.querySelector(".react-select__menu-list");
        if (menu) {
          const option = menu.childNodes[80];
          menu.scrollTo(0, option.offsetTop);
        }
      });
    }
  }, []);

  const reducer = useCallback((state, action) => {
    switch (action.type) {
      case "year":
        return updateState(state, "year", action.value);
      case "month":
        return updateState(state, "month", action.value);
      case "day":
        return updateState(state, "day", action.value);
      default:
        return state;
    }
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  // Initialisation à partir de `value` passé par Controller
  useEffect(() => {
    if (value) {
      const [y, m, d] = value.split("-").map(Number);
      dispatch({ type: "year", value: y });
      dispatch({ type: "month", value: m });
      dispatch({ type: "day", value: d });
    }
  }, [value]);

  // Met à jour RHF à chaque changement
  useEffect(() => {
    if (onChange && state.birthday) {
      onChange(state.birthday);
    }
  }, [state.birthday, onChange]);

  const handleYearChange = (option) =>
    dispatch({ type: "year", value: option.value });
  const handleMonthChange = (option) =>
    dispatch({ type: "month", value: option.value });
  const handleDayChange = (option) =>
    dispatch({ type: "day", value: option.value });

  return (
    <div className={styles.inputField}>
      {title && (
        <legend
          className={classnames(styles.formTitle, {
            [styles.required]: required,
            [styles.forbidChange]: forbidChange,
          })}
        >
          {title}
        </legend>
      )}
      <div className={styles.formGroup}>
        <SelectField
          name={`${name}-y`}
          placeholder="年"
          value={selectOption("year", state.year)}
          options={yearOptions()}
          onChange={handleYearChange}
          onMenuOpen={() => handleYearMenuOpen(state)}
          {..._props}
        />
        <SelectField
          name={`${name}-m`}
          placeholder="月"
          value={selectOption("month", state.month)}
          options={monthOptions()}
          onChange={handleMonthChange}
          {..._props}
        />
        <SelectField
          name={`${name}-d`}
          placeholder="日"
          value={selectOption("day", state.day)}
          options={dayOptions(state.daysInMonth)}
          onChange={handleDayChange}
          {..._props}
        />
      </div>
      {caption && <p className={styles.caption}>{caption}</p>}
      {error && <ErrorMessage msg={error} />}
    </div>
  );
});

BirthdayField.displayName = "BirthdayField";
export default BirthdayField;
